/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { fetchEventById, fetchUserById } from "../../services/eventService";
import FormattedContent from "../../components/Common/FormattedContent";
import { BASE_URL_IMAGE } from "../../config/api";
import LoadingSpinner from "../../components/Common/LoadingSpinner";
import "../../assets/css/EventDetails.css";

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadEvent = async () => {
      try {
        const eventData = await fetchEventById(id);
        setEvent(eventData);

        if (eventData.user_id) {
          const userData = await fetchUserById(eventData.user_id);
          setAuthor(userData);
        }
      } catch (err) {
        setError("Failed to load event details.");
      } finally {
        setLoading(false);
      }
    };

    loadEvent();
  }, [id]);

  const breadcrumbItems = [
    { label: "Events", path: "/events" },
    { label: event?.event_title || "View" },
  ];

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  if (loading) {
    return (
      <div className="main-wrapper">
        <Header />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content">
            <LoadingSpinner message="Loading event..." />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-wrapper">
        <Header />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content">
            <div className="error-container">
              <h3>Error</h3>
              <p>{error}</p>
              <button className="btn btn-primary" onClick={() => navigate("/events")}>
                Back to Events
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <div className="main-wrapper">
      <Header />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="row">
            {/* Main content */}
            <div className="col-lg-8 col-md-12">
              <div className="event-view">
                <article className="event-single-post">
                  <div className="event-header">
                    <div className="badge bg-primary">{event.event_category || "Event"}</div>
                    <h1 className="event-title">{event.event_title}</h1>
                    <div className="event-meta">
                      <div className="meta-item">
                        <img
                          className="author-avatar"
                          src={`${BASE_URL_IMAGE}/uploads/userProfiles/${author?.user_profile_img}`}
                          alt={author?.user_name}
                        />
                        <div className="author-info">
                          <span className="author-name">{author?.user_name || "Unknown Author"}</span>
                          <span className="author-designation">{author?.user_designation}</span>
                        </div>
                      </div>
                      <div className="meta-divider">•</div>
                      <div className="meta-item">
                        <FeatherIcon icon="calendar" size={16} />
                        <span>
                          {formatDate(event.event_startDate)} - {formatDate(event.event_endDate)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="event-image-container">
                    <img
                      className="event-featured-image"
                      src={`${BASE_URL_IMAGE}/uploads/eventImages/${event.event_img}`}
                      alt={event.event_title}
                    />
                  </div>

                  <div className="event-content">
                    <FormattedContent htmlContent={event.event_content} />
                    {event.event_tags && (
                      <div className="event-tags">
                        <h5>Tags:</h5>
                        <div className="tags-container">
                          {event.event_tags.split(",").map((tag, idx) => (
                            <span key={idx} className="tag">
                              #{tag.trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </article>
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-lg-4 col-md-12">
              <div className="event-sidebar">
                {/* Author Card */}
                <div className="author-profile-card">
                  <img
                    className="profile-image-circle"
                    alt="Author"
                    src={`${BASE_URL_IMAGE}/uploads/userProfiles/${author?.user_profile_img}`}
                  />
                  <div className="profile-info">
                    <h4 className="profile-name">{author?.user_name || "Unknown Author"}</h4>
                    <p className="profile-role">{author?.user_designation}</p>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="quick-actions-widget">
                  <h5>Quick Actions</h5>
                  <div className="action-buttons">
                    <Link to="/events" className="btn btn-outline-primary btn-block">
                      <FeatherIcon icon="arrow-left" size={16} className="me-2" />
                      Back to Events
                    </Link>
                    <Link
                      to={`/edit-event/${event.event_id}`}
                      className="btn btn-primary btn-block"
                    >
                      <FeatherIcon icon="edit" size={16} className="me-2" />
                      Edit Event
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div >
    </div >
  );
};

export default EventDetails;
